import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import CustomMaskedInput from "./CustomMaskedInput";

const InputTextPatrocinador = (props) => {
  const [valor, setValor] = useState(props.value || "");
  const [alerta, setAlerta] = useState(false);
  const [classErro, setClassErro] = useState("");
  const [touched, setTouched] = useState(false);
  const [isBlurValidated, setIsBlurValidated] = useState(false);
  const inputRef = useRef(null);

  // Gerar ID único se não fornecido
  const uniqueId = useMemo(() => {
    return props.id || `input-${Math.random().toString(36).substr(2, 9)}`;
  }, [props.id]);

  // Sincronizar valor externo apenas quando necessário
  useEffect(() => {
    if (props.value !== valor && !touched) {
      setValor(props.value || "");
    }
  }, [props.value, touched]);

  // Validação
  const validateValue = useCallback((value) => {
    if (props.requered && (!value || value.trim() === "")) {
      return "O campo é obrigatório";
    }

    if (props.type === "email" && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return "E-mail inválido";
      }
    }

    if (props.countCPF && value) {
      const numbers = value.replace(/\D/g, '');
      if (numbers.length !== 11) {
        return "CPF deve ter 11 dígitos";
      }
    }

    return null;
  }, [props.requered, props.type, props.countCPF]);

  // Função de comparação
  const comparador = useCallback((valor) => {
    if (props.comparador) {
      setAlerta(valor !== props.comparador);
    }
  }, [props.comparador]);

  // Handler de mudança otimizado
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    setValor(newValue);
    setTouched(true);

    // Validação em tempo real
    const error = validateValue(newValue);
    setClassErro(error ? "input-erro" : "");

    // Callback para o componente pai - sempre chamar
    if (props.onChange) {
      props.onChange(newValue);
    }

    // Comparação se necessário
    comparador(newValue);
  }, [props.onChange, validateValue, comparador]);

  // Handler de blur otimizado
  const handleBlur = useCallback((e) => {
    const currentValue = e.target.value;
    setTouched(true);
    setIsBlurValidated(true);

    // Validação final no blur
    const error = validateValue(currentValue);
    setClassErro(error ? "input-erro" : "");

    // Comparação se necessário
    comparador(currentValue);

    // Callback onBlur para o componente pai (se fornecido)
    if (props.onBlur) {
      props.onBlur(currentValue);
    }
  }, [validateValue, comparador, props.onBlur]);

  // Renderização condicional do input
  const renderInput = () => {
    const commonProps = {
      id: uniqueId,
      name: uniqueId,
      autoFocus: props.focus ?? false,
      value: valor,
      className: `form-control form-control-lg ${classErro}`,
      disabled: props.disabled || false,
      placeholder: props.placeholder || "",
      'aria-describedby': props.error ? `${uniqueId}-error` : undefined,
      'aria-invalid': !!(props.error || (touched && validateValue(valor))),
      ref: inputRef,
    };

    if (props.mascara) {
      // Mapear máscaras para o formato do CustomMaskedInput
      let maskType = props.mascara;
      if (props.mascara === "999.999.999-99") maskType = "cpf";
      else if (props.mascara === "(99) 99999-9999") maskType = "telefone";
      else if (props.mascara === "99999-999") maskType = "cep";
      else if (props.mascara === "99/99/9999") maskType = "data";

      return (
        <CustomMaskedInput
          {...commonProps}
          mask={maskType}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      );
    }

    return (
      <input
        {...commonProps}
        type={props.type || "text"}
        onChange={handleChange}
        onBlur={handleBlur}
      />
    );
  };

  // Determinar se há erro (externo via props ou interno)
  const internalError = touched ? validateValue(valor) : null;
  const hasError = props.error || internalError;
  const errorMessage = props.error || internalError;
  const isValid = touched && !hasError && valor;

  return (
    <div className={`${props.classNames || "mb-1"} input-wrapper 
      ${hasError ? 'has-error' : ''}
      ${isValid ? 'has-success' : ''}
      ${isBlurValidated ? 'blur-validated' : ''}
    `}>
      <label htmlFor={uniqueId} className="form-label label-input">
        {props.label}
        {props.requered && <span className="text-red">*</span>}
      </label>

      <div className="input-container">
        {renderInput()}
        
        {/* Indicadores removidos para evitar duplicação visual */}
      </div>

      {/* Mensagens de erro */}
      {errorMessage && (
        <div id={`${uniqueId}-error`} className={`erro-formulario ${isBlurValidated ? 'blur-error' : ''}`} role="alert">
          <i className="fas fa-exclamation-circle me-1"></i>
          {errorMessage}
        </div>
      )}

      {/* Alerta de comparação */}
      {alerta && (
        <div id={`${uniqueId}-comparison-error`} className={`erro-formulario ${isBlurValidated ? 'blur-error' : ''}`} role="alert">
          <i className="fas fa-exclamation-circle me-1"></i>
          Os valores não coincidem
        </div>
      )}

      {/* Dica de ajuda */}
      {props.hint && !hasError && (
        <div className="input-hint">
          <i className="fas fa-info-circle me-1"></i>
          {props.hint}
        </div>
      )}
    </div>
  );
};

export default InputTextPatrocinador;

